import '../../styles/restScreens.css';
import { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import EntityFormModal from '../entities/EntityFormModal';
import BrandingPanel from '../entities/BrandingPanel';
import { ENTITY_CODE_PATTERN, ENTITY_CODE_RULE, normalizeEntityCode, suggestEntityCode } from '../../lib/entityCode';

// BL-170 Gate 5 — the first-owner guided setup shell.
//
// Fires exactly ONCE for a brand-new org, right after Gate 2's provisioning and Gate 4's
// industry/tier wizard (OnboardingWizard.jsx, untouched by this file) have both finished, and
// lands on the same dashboard those already land on. It does not re-fire on later logins —
// CRMApp.jsx only mounts this when the org's own orgs.first_owner_setup_completed_at is still
// NULL, and this component's job is to set that column once the owner finishes or skips.
//
// Three short steps, each skippable, and the whole flow is skippable too:
//   1. Confirm/edit the org's display name (orgs.name) — new UI, this is the only step that
//      isn't a reuse.
//   2. Confirm/edit the founding entity's display name — reuses EntityFormModal as-is; this
//      shell never re-implements its validation/save logic, it only supplies the entity and
//      handles what happens after Save/Cancel.
//   3. Pick a brand colour — reuses BrandingPanel as-is for the same reason; the shell just
//      hands it the one founding entity and a save function.
const TOTAL_STEPS = 3;

function StepChrome({ step, title, onSkipAll, children }) {
    return (
        <div data-m6-layout="onboarding" data-m6-component="FirstOwnerSetupWizard" className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 px-4 py-10">
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-1">
                            Step {step} of {TOTAL_STEPS}
                        </div>
                        <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
                    </div>
                    <button
                        type="button"
                        onClick={onSkipAll}
                        className="text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
                    >
                        Skip for now
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function FirstOwnerSetupWizard({ user, orgId, theme = 'light', onComplete }) {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [step, setStep] = useState(1);
    const [orgName, setOrgName] = useState('');
    const [savingOrgName, setSavingOrgName] = useState(false);
    const [orgNameError, setOrgNameError] = useState(null);
    const [foundingEntity, setFoundingEntity] = useState(null);
    const [finishing, setFinishing] = useState(false);
    // UX pass 2026-10-25: the company code (founding entity's entity_code). Without it every New Staff save is refused,
    // so step 1 asks for it (suggested from the name) and skipping saves the suggestion rather than leaving it empty.
    const [companyCode, setCompanyCode] = useState('');
    const [codeTouched, setCodeTouched] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        async function load() {
            if (!orgId) {
                setLoadError('Missing organization context.');
                setLoading(false);
                return;
            }

            const [{ data: orgRow, error: orgError }, { data: entityRows, error: entityError }] = await Promise.all([
                supabaseClient.from('orgs').select('name').eq('id', orgId).maybeSingle(),
                supabaseClient
                    .from('entities')
                    .select('id, org_id, parent_entity_id, level_type, name, display_label, is_booking_node, entity_code, status, color_palette')
                    .eq('org_id', orgId)
                    .order('created_at', { ascending: true })
                    .limit(1)
            ]);

            if (isCancelled) return;

            if (orgError || entityError) {
                setLoadError((orgError || entityError)?.message || 'Unable to load your organization.');
                setLoading(false);
                return;
            }

            setOrgName(orgRow?.name || '');
            const founding = (entityRows && entityRows[0]) || null;
            setFoundingEntity(founding);
            setCompanyCode(founding?.entity_code || suggestEntityCode(orgRow?.name || founding?.name || ''));
            setLoading(false);
        }

        load();

        return () => {
            isCancelled = true;
        };
    }, [orgId]);

    // Saves the code on the founding record when it has none. Returns an error message, or '' when the code is set.
    async function ensureCompanyCode(value) {
        if (!foundingEntity || ENTITY_CODE_PATTERN.test(foundingEntity.entity_code || '')) return '';
        const code = normalizeEntityCode(value || suggestEntityCode(orgName || foundingEntity.name));
        if (!ENTITY_CODE_PATTERN.test(code)) return ENTITY_CODE_RULE;
        const { data, error } = await supabaseClient
            .from('entities')
            .update({ entity_code: code, updated_by: user?.id })
            .eq('id', foundingEntity.id)
            .select()
            .single();
        if (error) return error.message || 'Unable to save the company code.';
        setFoundingEntity(data);
        return '';
    }

    async function markComplete() {
        setFinishing(true);
        const codeError = await ensureCompanyCode(companyCode);
        if (codeError) console.error('UX pass: unable to save the company code during setup:', codeError);
        if (orgId) {
            const { error } = await supabaseClient
                .from('orgs')
                .update({ first_owner_setup_completed_at: new Date().toISOString() })
                .eq('id', orgId);
            if (error) {
                console.error('BL-170 Gate 5: unable to record guided setup completion:', error);
            }
        }
        setFinishing(false);
        onComplete?.();
    }

    function handleSkipAll() {
        if (finishing) return;
        markComplete();
    }

    async function handleSaveOrgName() {
        setOrgNameError(null);
        const trimmed = orgName.trim();
        if (!trimmed) {
            setOrgNameError('Organization name is required.');
            return;
        }
        if (!orgId) {
            setOrgNameError('Missing organization context.');
            return;
        }
        const code = normalizeEntityCode(companyCode);
        if (!ENTITY_CODE_PATTERN.test(code)) {
            setOrgNameError(`Company code: ${ENTITY_CODE_RULE}`);
            return;
        }
        setSavingOrgName(true);
        const { error } = await supabaseClient.from('orgs').update({ name: trimmed }).eq('id', orgId);
        if (error) {
            setSavingOrgName(false);
            setOrgNameError(error.message || 'Unable to save the organization name.');
            return;
        }
        const codeError = await ensureCompanyCode(code);
        setSavingOrgName(false);
        if (codeError) {
            setOrgNameError(`Company code: ${codeError}`);
            return;
        }
        setStep(2);
    }

    async function handleSkipStep(nextStep) {
        if (nextStep === 2) await ensureCompanyCode(companyCode);
        if (nextStep > TOTAL_STEPS) {
            markComplete();
            return;
        }
        setStep(nextStep);
    }

    async function handleSaveEntityName(payload) {
        if (!foundingEntity) return;
        const { data, error } = await supabaseClient
            .from('entities')
            .update({ ...payload, updated_by: user?.id })
            .eq('id', foundingEntity.id)
            .select()
            .single();
        if (error) throw error;
        setFoundingEntity(data);
        setStep(3);
    }

    async function handleSaveColor(entity, hexOrNull) {
        const color_palette = hexOrNull
            ? { ...(entity.color_palette || {}), primary: hexOrNull }
            : null;
        const { data, error } = await supabaseClient
            .from('entities')
            .update({ color_palette, updated_by: user?.id })
            .eq('id', entity.id)
            .select()
            .single();
        if (error) throw error;
        setFoundingEntity(data);
    }

    if (loading) {
        return (
            <div data-m6-layout="onboarding" data-m6-component="FirstOwnerSetupWizard" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-orange-100">
                <div className="text-xl text-gray-600">Loading…</div>
            </div>
        );
    }

    if (loadError || !foundingEntity) {
        return (
            <StepChrome step={step} title="Let's finish setting up your organization" onSkipAll={handleSkipAll}>
                <p className="text-sm text-red-600">
                    {loadError || 'Unable to find your founding Organization record.'} You can finish this later from Staff &amp; Roles › Structure.
                </p>
            </StepChrome>
        );
    }

    if (step === 1) {
        return (
            <StepChrome step={1} title="Confirm your organization's name" onSkipAll={handleSkipAll}>
                <p className="text-sm text-gray-500 mb-4">
                    This is the name your team and clients will see. You can always change it later in Settings.
                </p>
                {orgNameError && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-4">{orgNameError}</div>
                )}
                <label htmlFor="first-owner-org-name" className="block text-sm font-medium text-gray-700 mb-1">Organization name</label>
                <input
                    id="first-owner-org-name"
                    type="text"
                    value={orgName}
                    onChange={(e) => {
                        setOrgName(e.target.value);
                        if (!codeTouched && !foundingEntity?.entity_code) setCompanyCode(suggestEntityCode(e.target.value));
                    }}
                    placeholder="Your organization name"
                    className="w-full min-h-[44px] px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white border-gray-300 text-gray-900 mb-4"
                />
                <label htmlFor="first-owner-company-code" className="block text-sm font-medium text-gray-700 mb-1">Company code</label>
                <input
                    id="first-owner-company-code"
                    type="text"
                    value={companyCode}
                    maxLength={5}
                    disabled={Boolean(foundingEntity?.entity_code)}
                    aria-describedby="first-owner-company-code-help"
                    onChange={(e) => { setCodeTouched(true); setCompanyCode(normalizeEntityCode(e.target.value)); }}
                    className="w-40 min-h-[44px] px-3 py-2 border rounded-lg font-mono uppercase bg-white border-gray-300 text-gray-900"
                />
                <p id="first-owner-company-code-help" className="text-xs text-gray-500 mt-1 mb-6">
                    A short code that starts every employee ID and confirmation number. We suggested one from your name; you can change it. {ENTITY_CODE_RULE}
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => handleSkipStep(2)}
                        disabled={savingOrgName}
                        className="px-4 py-2 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Skip this step
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveOrgName}
                        disabled={savingOrgName}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50"
                    >
                        {savingOrgName ? 'Saving…' : 'Save & Continue'}
                    </button>
                </div>
            </StepChrome>
        );
    }

    if (step === 2) {
        return (
            <>
                <StepChrome step={2} title="Confirm your Organization record's name" onSkipAll={handleSkipAll}>
                    <p className="text-sm text-gray-500">
                        This is the root of your Organization structure. Confirm or edit it below.
                    </p>
                </StepChrome>
                <EntityFormModal
                    mode="edit"
                    entity={foundingEntity}
                    entities={[foundingEntity]}
                    descendantIds={[]}
                    onClose={() => handleSkipStep(3)}
                    onSave={handleSaveEntityName}
                    theme={theme}
                />
            </>
        );
    }

    // step === 3
    return (
        <StepChrome step={3} title="Pick a brand colour" onSkipAll={handleSkipAll}>
            <p className="text-sm text-gray-500 mb-4">
                This paints buttons, links and the active menu item for everyone who logs in. Click Edit to preview and save.
            </p>
            <BrandingPanel entities={[foundingEntity]} theme={theme} onSaveColor={handleSaveColor} />
            <div className="flex justify-end gap-3 mt-6">
                <button
                    type="button"
                    onClick={markComplete}
                    disabled={finishing}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50"
                >
                    {finishing ? 'Finishing…' : 'Finish'}
                </button>
            </div>
        </StepChrome>
    );
}

export default FirstOwnerSetupWizard;
